import React, { useEffect, useRef, useState } from "react";

// static imports (ensure these paths exist in your project)
import anaestheticImg from "../assets/images/anaesthetic.jpg";
import communityImg from "../assets/images/slide/22.jpg";
import biomedicalImg from "../assets/images/biomedical.jpg";
import nursingImg from "../assets/images/slide/16.jpg";

// default slides array (rename so it doesn't conflict with prop)
const DEFAULT_SLIDES = [
  {
    key: "anaesthetic",
    name: "Anaesthetic Technicians",
    image: anaestheticImg,
  },
  { key: "community", name: "School of Community Health", image: communityImg },
  {
    key: "biomedical",
    name: "Federal School of Biomedical Engineering",
    image: biomedicalImg,
  },
  {
    key: "nursing",
    name: "Department of Nursing Sciences",
    image: nursingImg,
  },
];

function getImageUrlFromSlide(s) {
  if (!s) return "";
  // common shapes: s.image (string), s.img (string), s.url, or imported module object with default
  if (typeof s.image === "string" && s.image) return s.image;
  if (typeof s.img === "string" && s.img) return s.img;
  if (typeof s.url === "string" && s.url) return s.url;
  // handle possible imported module object { default: 'url' }
  if (s.image && typeof s.image === "object") {
    if (s.image.default && typeof s.image.default === "string")
      return s.image.default;
    if (s.image.src && typeof s.image.src === "string") return s.image.src;
  }
  if (s.img && typeof s.img === "object") {
    if (s.img.default && typeof s.img.default === "string")
      return s.img.default;
    if (s.img.src && typeof s.img.src === "string") return s.img.src;
  }
  return "";
}

const SchoolHeroSlider = ({ slides: slidesProp, onViewInstructions }) => {
  // pick slides provided via prop OR default list
  const slidesToUse =
    Array.isArray(slidesProp) && slidesProp.length
      ? DEFAULT_SLIDES
      : DEFAULT_SLIDES;

  // For sanity/debugging — remove in production
  // eslint-disable-next-line no-console
  console.log(
    "SchoolHeroSlider slides:",
    slidesToUse.map((s) => ({ key: s.key, image: getImageUrlFromSlide(s) })),
  );

  const total = (slidesToUse?.length || 0) + 1; // +1 intro
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  function start() {
    stop();
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % total);
    }, 5500);
  }
  function stop() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function go(n) {
    setIdx((i) => (n + total) % total);
  }
  function prev() {
    go(idx - 1);
  }
  function next() {
    go(idx + 1);
  }

  return (
    <section
      className="schslider-root"
      onMouseEnter={stop}
      onMouseLeave={start}
      aria-roledescription="carousel"
    >
      <div
        className="schslider-inner"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {/* INTRO SLIDE */}
        <div
          className="schslider-slide schslider-intro"
          aria-hidden={idx !== 0}
        >
          <div className="schslider-intro-wrap">
            <div className="schslider-intro-left">
              <h1 className="schslider-intro-title">
                Browse the Schools at Our Institution
              </h1>
              <p className="schslider-intro-sub">
                Click a school below to view our programmes.
              </p>

              <div className="schslider-cta-row">
                <button
                  className="schslider-cta-primary"
                  onClick={onViewInstructions}
                >
                  📄 Admission Instructions
                </button>
                <button
                  className="schslider-cta-ghost"
                  onClick={() => (window.location.hash = "#schools-grid")}
                >
                  View News & Updates
                </button>
              </div>
            </div>

            <div className="schslider-intro-right" aria-hidden>
              <div className="schslider-emblem" />
            </div>
          </div>
        </div>

        {/* IMAGE SLIDES */}
        {slidesToUse.map((s, i) => {
          const variant = (i % 3) + 1;
          const imgUrl = getImageUrlFromSlide(s); // robust normalized url string
          return (
            <div
              key={s.key}
              className={`schslider-slide schslider-image-slide schslider-variant-${variant}`}
            >
              {/* cover image element (more reliable for imported assets) */}
              <div className="schslider-image">
                {imgUrl ? (
                  // show real <img> (easier to debug network / 404s in devtools)
                  <img
                    className="schslider-image-img"
                    src={imgUrl}
                    alt={s.name}
                  />
                ) : (
                  // fallback visual so slide area doesn't vanish
                  <div className="schslider-image-fallback">No image</div>
                )}
              </div>

              {/* overlay variants unchanged */}
              {variant === 1 && (
                <div className="schslider-wow schslider-center">
                  <div className="schslider-wow-inner">
                    <div className="schslider-wow-eyebrow">Welcome to</div>
                    <h2 className="schslider-wow-title">{s.name}</h2>
                    <button
                      className="schslider-wow-cta"
                      onClick={() =>
                        (window.location.hash = `#school-${s.key}`)
                      }
                    >
                      View Programmes
                    </button>
                  </div>
                </div>
              )}
              {variant === 2 && (
                <div className="schslider-glass schslider-glass-left">
                  <div className="schslider-glass-title">{s.name}</div>
                  <div className="schslider-glass-actions">
                    <button
                      className="schslider-glass-btn"
                      onClick={() =>
                        (window.location.hash = `#school-${s.key}`)
                      }
                    >
                      View Programmes
                    </button>
                  </div>
                </div>
              )}
              {variant === 3 && (
                <div className="schslider-glass schslider-glass-right">
                  <div className="schslider-right-stack">
                    <div className="schslider-right-label">School</div>
                    <div className="schslider-right-name">{s.name}</div>
                    <button
                      className="schslider-glass-btn"
                      onClick={() =>
                        (window.location.hash = `#school-${s.key}`)
                      }
                    >
                      View Programmes
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* arrows */}
      <button
        className="schslider-arrow schslider-prev"
        onClick={prev}
        aria-label="Previous slide"
      >
        <span className="schslider-chev">‹</span>
        <span className="schslider-circlewrap">
          <span className="schslider-circle schslider-circle-main" />
          <span className="schslider-circle schslider-circle-overlay" />
        </span>
      </button>

      <button
        className="schslider-arrow schslider-next"
        onClick={next}
        aria-label="Next slide"
      >
        <span className="schslider-chev">›</span>
        <span className="schslider-circlewrap">
          <span className="schslider-circle schslider-circle-main" />
          <span className="schslider-circle schslider-circle-overlay" />
        </span>
      </button>

      {/* dots */}
      <div className="schslider-dots" role="tablist">
        <button
          className={`schslider-dot ${idx === 0 ? "active" : ""}`}
          onClick={() => go(0)}
          aria-label="Intro"
        />
        {slidesToUse.map((s, i) => (
          <button
            key={s.key}
            className={`schslider-dot ${idx === i + 1 ? "active" : ""}`}
            onClick={() => go(i + 1)}
            aria-label={s.name}
          />
        ))}
      </div>
    </section>
  );
};

export default SchoolHeroSlider;
